import type { Node as PMNode } from "@tiptap/pm/model";
import type { LabelOwner } from "./labelKeys";

/** The kind a label belongs to, from the node type that holds it. */
const KIND: Record<string, string> = {
  tableBlock: "table",
  figureBlock: "figure",
  equationBlock: "equation",
  heading: "heading",
  algorithmBlock: "algorithm",
  codeBlockNode: "code",
  theoremBlock: "theorem",
};

/**
 * Every label in the document as it stands in the editor — including edits not
 * yet saved.
 *
 * Read from the live document rather than the references endpoint, because a
 * collision check has to see the table the author labelled thirty seconds ago,
 * and the endpoint reflects the last save.
 *
 * `exceptPos` excludes the block being edited: a block does not collide with
 * itself.
 */
export function documentLabels(doc: PMNode, exceptPos?: number): LabelOwner[] {
  const owners: LabelOwner[] = [];
  doc.descendants((node, pos) => {
    if (pos === exceptPos) return false;
    const kind = KIND[node.type.name];
    if (!kind) return true;

    const label = node.attrs.label;
    if (typeof label === "string" && label.trim()) owners.push({ key: label.trim(), kind });

    // A figure's panels are targets in their own right.
    const subs = node.attrs.subfigures;
    if (Array.isArray(subs)) {
      for (const sub of subs) {
        const k = (sub as { label?: unknown })?.label;
        if (typeof k === "string" && k.trim()) owners.push({ key: k.trim(), kind: "figure" });
      }
    }
    return false;
  });
  return owners;
}

/** Just the keys, for derivation. */
export function takenKeys(doc: PMNode, exceptPos?: number): Set<string> {
  return new Set(documentLabels(doc, exceptPos).map((o) => o.key));
}
